/**
 * Group citizenship ceremony capacity.
 *
 * `GET {blApi}/citizenship/availability/{company}/{service}/{date}` answers one question
 * only: how many places are left on that ceremony — the service's
 * `citizenship_group_max` (or the per-date `citizenship_overrides_{client}` value) minus
 * the rows already in `citizenship_claims_{client}` for that datetime. It is never told
 * how many people this booking is for, so it can report capacity but never police it.
 *
 * The party-size comparison therefore belongs to the journey, and it has to be made the
 * same way at every point the customer can commit to a slot: the calendar when they pick
 * a time, and the summary page before the pre-basket call hands them to the payment
 * gateway. Both read this file so the two cannot drift apart.
 */

/** The form-builder field holding the attendee count for a group ceremony. */
export const CITIZENSHIP_ATTENDEES_FIELD =
	'how_many_attendees_and_their_details_including_if_adult_or_child';

export interface CitizenshipPartySize {
	/** Places this booking will take on the ceremony. Never less than one. */
	places: number;
	/** The question was answered, but not with a number we can use. */
	unusableAnswer: boolean;
}

/**
 * The attendee answer for the selected schema.
 *
 * The form-builder keeps its own answers in sessionStorage rather than in the datastore,
 * keyed by schema id, which is why this is read straight from storage on every page that
 * needs it.
 */
export function citizenshipAttendeesAnswer(selectedSchema: any): any {
	let answers: any;
	try {
		answers = JSON.parse(sessionStorage.getItem('form_builder_answers') || '{}');
	} catch (err) {
		console.error('form_builder_answers could not be parsed', err);
		return undefined;
	}
	return answers?.[selectedSchema]?.[CITIZENSHIP_ATTENDEES_FIELD];
}

/**
 * How many places on the ceremony this booking will consume.
 *
 * "Myself" bookings never display the attendees question, so a missing or blank answer
 * means a party of one rather than something that blocks the journey. An answer that is
 * there but unusable is reported separately: the calendar can legitimately send the
 * customer back to the question, while the summary page — where the details are already
 * behind them — must not turn a malformed answer into a refused payment.
 *
 * The answer is stored as the raw scalar the customer gave, but it is unwrapped from a
 * `{ value }` shape as well, so a form-builder that starts wrapping answers cannot
 * silently reduce every party to one person.
 */
export function citizenshipPartySize(attendeesAnswer: any): CitizenshipPartySize {
	const raw =
		attendeesAnswer && typeof attendeesAnswer === 'object' && 'value' in attendeesAnswer
			? attendeesAnswer.value
			: attendeesAnswer;

	const answered = raw !== undefined && raw !== null && `${raw}`.trim() !== '';
	if (!answered) {
		return { places: 1, unusableAnswer: false };
	}

	const parsed = parseInt(raw, 10);
	if (!Number.isFinite(parsed) || parsed <= 0) {
		return { places: 1, unusableAnswer: true };
	}

	return { places: parsed, unusableAnswer: false };
}

/** Whether the party fits in what citizenshipAvailability reported. */
export function hasCapacityForParty(availability: any, requiredPlaces: number): boolean {
	const slots = Number(availability?.slots);
	if (!Number.isFinite(slots) || slots <= 0) {
		return false;
	}
	return requiredPlaces <= slots;
}

/** The slot count to show the customer, for an availability response of any shape. */
export function availableSlotsForDisplay(availability: any): number {
	const slots = Number(availability?.slots);
	return Number.isFinite(slots) ? slots : 0;
}

/**
 * The three answers a capacity check can give.
 *
 * `api.service.citizenshipAvailability` swallows every failure and returns the error
 * *message string*, so an unreachable endpoint arrives here looking exactly like a
 * response with no places left. That distinction does not matter on the calendar, where
 * the customer can simply pick a different time, but it matters a great deal on the
 * summary page: telling someone their ceremony is full when all that happened was a
 * failed request would send them away from a booking they could have completed.
 */
export type CitizenshipCapacityOutcome = 'available' | 'full' | 'unknown';

/**
 * Whether the party still fits, and whether we were actually told.
 *
 * 'unknown' means the endpoint gave us nothing we can compare against — the caller
 * should say nothing to the customer rather than invent a capacity problem.
 */
export function citizenshipCapacityOutcome(
	availability: any,
	requiredPlaces: number
): CitizenshipCapacityOutcome {
	if (!availability || typeof availability !== 'object') {
		return 'unknown';
	}
	if (!Number.isFinite(Number(availability.slots))) {
		return 'unknown';
	}
	return hasCapacityForParty(availability, requiredPlaces) ? 'available' : 'full';
}

/**
 * Reservations (DEL-51).
 *
 * The capacity check above reserves nothing, so everyone asking inside the same few
 * minutes is told the same number and all of them proceed. A reservation turns that
 * read into a hold: picking a ceremony takes the party's places for fifteen minutes
 * against the basket token, and availability then subtracts live holds.
 *
 * Reservations are OFF until a client's config enables them, and the controller
 * answers 404 while they are — so 'unavailable' below is the normal state today and
 * must never be shown to a customer as a problem.
 */
export type CitizenshipReservationOutcome =
	/** The places are held for this session. */
	| 'reserved'
	/** Definitively refused — someone else has them. Safe to tell the customer. */
	| 'full'
	/** Reservations are not switched on for this client. Say nothing, carry on. */
	| 'unavailable'
	/** We were not told. Say nothing rather than invent a capacity problem. */
	| 'unknown';

/**
 * Read a reserve/renew response, from either the success or the error path.
 *
 * Angular rejects on any non-2xx, so a 409 arrives here as a thrown HttpErrorResponse
 * rather than a return value — both shapes carry `status`, which is why this reads that
 * and not the promise's outcome.
 *
 * Only a 409 is a real "full". A 404 means the route is not enabled for this client, and
 * anything else — network, 5xx, a token problem — is 'unknown'. Both of those let the
 * journey continue: bl-adelante-api re-checks before any money moves, so a front end that
 * failed to reserve cannot cause an overbooking on its own, whereas a front end that
 * wrongly says "full" sends away a customer who could have booked.
 */
export function citizenshipReservationOutcome(response: any): CitizenshipReservationOutcome {
	const status = Number(response?.status);
	if (!Number.isFinite(status)) return 'unknown';
	if (status >= 200 && status < 300) return 'reserved';
	if (status === 409) return 'full';
	if (status === 404) return 'unavailable';
	return 'unknown';
}

/**
 * Places left according to a 409 body, for the "only N places left" message.
 *
 * The controller returns `remaining` alongside capacity/booked/held. Angular puts the
 * body on `.error` for a rejected response and on `.body` for a resolved one.
 */
export function citizenshipRemainingFrom(response: any): number {
	const body = response?.error ?? response?.body ?? response;
	const remaining = Number(body?.remaining);
	return Number.isFinite(remaining) && remaining > 0 ? remaining : 0;
}

/**
 * Places to HOLD for the party: the attendee answer, but never fewer than the applicants
 * looked up.
 *
 * bl-adelante-api verifies the hold against the number of Home Office lookups — one claim
 * row is written per lookup — and refuses the payment if the hold is for fewer. This app
 * sizes the party from the attendee answer instead (RBKC floors its count at the lookups
 * already), so the two can disagree: a blank answer is a party of one whatever was looked
 * up. Holding the larger of the two keeps a legitimate payment from being refused.
 */
export function citizenshipHoldPlaces(partyPlaces: number, lookupResponses: any): number {
	const lookups = Array.isArray(lookupResponses)
		? lookupResponses.filter((response: any) => response !== null && response !== undefined).length
		: 0;
	const party = Number.isFinite(partyPlaces) && partyPlaces > 0 ? partyPlaces : 1;
	return Math.max(party, lookups);
}

/** The applicant record ids this booking will occupy, so the hold can name them. */
export function citizenshipClaimIds(lookupResponses: any): any[] {
	// Each entry IS the applicant's claim record — the confirmation page passes these
	// straight to updateReference, which writes booking_datetime onto `id`. So these are
	// exactly the rows the hold needs to name.
	return (Array.isArray(lookupResponses) ? lookupResponses : [])
		.filter((response: any) => response && response.id)
		.map((response: any) => response.id);
}
